import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { switchMap, map, catchError, withLatestFrom, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { AiActions } from '@store/actions/ai.actions';
import { FrameActions } from '@store/actions/frame.actions';
import { AppState } from '@store/app.state';
import { selectSettings } from '@store/selectors/settings.selector';
import { PixelFrame, createBlankFrame, celKey } from '@models/frame.model';
import { AiGenerateRequest } from '@models/ai-generation.model';
import { CollaborationService } from '@services/collaboration.service';
import { selectActiveLayerId } from '@store/selectors/frame.selectors';

function styleGuide(style: string): string {
  const guides: Record<string, string> = {
    retro: 'limited 8-color palette, hard pixel edges, classic 8-bit game look',
    neon: 'bright cyan/magenta/yellow on near-black background, glowing saturated colors',
    earthy: 'browns, greens, tans, rust reds - muted natural tones',
    pastel: 'soft pinks, lavenders, mint greens, light yellows',
    monochrome: 'shades of one color family, dark to light'
  };
  return guides[style] ?? 'vibrant, varied colors';
}

async function callAnthropicApi(request: AiGenerateRequest): Promise<PixelFrame[]> {
  const { prompt, frameCount, style, apiKey, gridW, gridH } = request;

  const systemPrompt = `You are a pixel art animation generator. Use a COMPACT palette+index format.

FORMAT (return ONLY this JSON, no markdown, no explanation):
{
  "palette": ["#hex0","#hex1",...],
  "frames": [
    [row0indices, row1indices, ...],
    ...
  ]
}

Rules:
- palette: up to 16 hex colors for the whole animation
- Each frame: array of ${gridH} rows, each row is a string of ${gridW} single hex digits (0-f = palette index 0-15)
- Example row of 16px wide: "0001233210012330"
- Grid size: ${gridW} wide x ${gridH} tall
- Generate exactly ${frameCount} frames
- Style: ${styleGuide(style)}
- Make smooth animation - subject visibly moves/changes across frames
- Index 0 = background color (usually dark/black)`;

  const userMsg = `Pixel art animation: "${prompt}"
${frameCount} frames, ${gridW}x${gridH} grid. Make it detailed - the scene should clearly progress across all ${frameCount} frames.`;

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true'
    },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 32000,
      system: systemPrompt,
      messages: [{ role: 'user', content: userMsg }]
    })
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error?.message ?? `HTTP ${response.status}`);
  }

  const data = await response.json();
  if (data.error) throw new Error(data.error.message);

  const rawText: string = data.content.map((b: any) => b.text ?? '').join('').trim();

  // Robust JSON extraction
  let parsed: any = null;
  const attempts = [
    () => JSON.parse(rawText),
    () => JSON.parse(rawText.replace(/^```[\w]*\n?|\n?```$/gm, '').trim()),
    () => { const m = rawText.match(/\{[\s\S]*\}/); if (!m) throw new Error('no block'); return JSON.parse(m[0]); },
    () => { const i = rawText.indexOf('"palette"'); const s = rawText.lastIndexOf('{', i); return JSON.parse(rawText.slice(s)); }
  ];
  for (const attempt of attempts) {
    try { parsed = attempt(); break; } catch { /* next */ }
  }

  if (!parsed?.palette || !parsed?.frames) {
    throw new Error(`Could not parse AI response. Stop reason: ${data.stop_reason}`);
  }

  const palette: string[] = parsed.palette;
  const frames: PixelFrame[] = [];

  for (const frameRows of parsed.frames) {
    if (!Array.isArray(frameRows)) continue;
    const f = createBlankFrame(gridW, gridH);
    (frameRows as any[]).forEach((row: any, r: number) => {
      if (r >= gridH) return;
      const rowStr = String(row);
      for (let c = 0; c < gridW && c < rowStr.length; c++) {
        const idx = parseInt(rowStr[c], 16);
        if (!isNaN(idx) && palette[idx]) {
          f.pixels[r][c] = palette[idx];
        }
      }
    });
    frames.push(f);
  }

  if (!frames.length) throw new Error('No valid frames decoded from AI response');
  return frames;
}

@Injectable()
export class AiEffects {
  private actions$ = inject(Actions);
  private store = inject(Store<AppState>);
  private collabSvc = inject(CollaborationService);

  generateFrames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiActions.generateFrames),
      withLatestFrom(this.store.select(selectSettings)),
      switchMap(([{ request }, settings]) => {
        const enrichedRequest: AiGenerateRequest = {
          ...request,
          gridW: request.gridW || settings.Resolution.width,
          gridH: request.gridH || settings.Resolution.height
        };
        return from(callAnthropicApi(enrichedRequest)).pipe(
          map(frames => AiActions.generateFramesSuccess({ frames })),
          catchError(err => of(AiActions.generateFramesFailure({ error: err.message ?? String(err) })))
        );
      })
    )
  );

  // On success, append generated frames to the frame store
  appendGeneratedFrames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiActions.generateFramesSuccess),
      withLatestFrom(this.store.select(selectActiveLayerId)),
      tap(([{ frames }, _layerId]) => {
        const s = this.collabSvc.collabSettings$.value;
        if (this.collabSvc.isInRoom$.value && s.broadcastAiGenerations) {
          for (const frame of frames) {
            this.collabSvc.broadcastFrame(frame.id, frame.pixels, s.canvasWidth, s.canvasHeight);
          }
        }
      }),
      map(([{ frames }, activeLayerId]) => {
        // Build cels so AI pixels become part of the active layer, not just the composite.
        const cels: Record<string, string[][]> = {};
        if (activeLayerId) {
          for (const frame of frames) {
            cels[celKey(frame.id, activeLayerId)] = frame.pixels;
          }
        }
        return FrameActions.appendFrames({ frames, cels });
      })
    )
  );
}
